import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getAppointmentDetails, getEvents, getQWIByQuoteId } from 'api/api'
import { SubStatusEnum } from '../../../enums/enums'
import { formatExistingTimeOrReturn } from 'utils/utils'
import { statusOptions } from '../schedule-calendar/helper'

dayjs.extend(utc)

// CAR-4034 — older bookings never saved the appointment on the quote; their sub-status is the only trace.
export const BOOKED_SUB_STATUSES = [SubStatusEnum.SCHEDULED.value, SubStatusEnum.MISSED.value]

// An appointment in one of these states can no longer be edited, even if its start is still ahead.
const CLOSED_APPOINTMENT_STATUSES = ['DELIVERED', 'CANCELLED']

export const getQuoteAppointmentId = (qwi) => qwi?.appointmentId ?? qwi?.appointment_id ?? null

// The scheduler returns the start as a UTC date plus a [hour, minute] pair (see formatExistingTimeOrReturn).
const appointmentStartUtc = (appointment) => {
  const date = appointment?.appointment_date
  const time = Array.isArray(appointment?.start_time) ? appointment.start_time : `${appointment?.start_time ?? ''}`.split(':')
  if (!date || time.length < 2 || time[0] === '') return null
  const start = dayjs.utc(`${date}T${String(time[0]).padStart(2, '0')}:${String(time[1]).padStart(2, '0')}:00Z`)
  return start.isValid() ? start : null
}

export const isAppointmentUpcoming = (appointment, now = dayjs()) => {
  const start = appointmentStartUtc(appointment)
  return Boolean(start && start.isAfter(now))
}

export const canEditAppointment = (appointment, now = dayjs()) =>
  isAppointmentUpcoming(appointment, now) && !CLOSED_APPOINTMENT_STATUSES.includes(appointment?.status)

// e.g. "Fri, Oct 31, 12:00 PM – 1:30 PM", in the scheduler organisation's time zone like the Scheduler shows it.
export const formatAppointmentWindow = (appointment) => {
  const formatted = formatExistingTimeOrReturn(
    appointment?.appointment_date,
    appointment?.start_time,
    appointment?.organisation?.timezone_at || 'UTC',
    appointment?.duration
  )
  if (!formatted?.newDate) return ''
  const start = dayjs(`${formatted.newDate}T${formatted.newTime}`)
  const when = start.format('ddd, MMM D, h:mm A')
  return appointment?.duration ? `${when} – ${start.add(appointment.duration, 'minute').format('h:mm A')}` : when
}

// e.g. "Client A · 2021 Lexus ES 250 · RO-VIN-005827"
export const appointmentDetailsLine = (appointment) => {
  const client = appointment?.client?.business_name || appointment?.client?.full_name
  const vehicle = appointment?.vehicle
  const car = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ')
  return [client, car, vehicle?.repair_order_num].filter(Boolean).join(' · ')
}

export const appointmentCreatedBy = (appointment) => appointment?.created_by?.name || appointment?.createdBy?.name || null

export const appointmentStatusLabel = (status) => statusOptions.find((option) => option.id === status)?.name || status || ''

// A past appointment that never moved on from Scheduled was missed.
export const appointmentStatusChip = (appointment, now = dayjs()) => {
  const status = appointment?.status
  if (status === 'SCHEDULED' && !isAppointmentUpcoming(appointment, now)) return { label: 'Missed', missed: true }
  return { label: appointmentStatusLabel(status), missed: false }
}

/**
 * What the quote is already booked with, or null when it has no appointment.
 *  - { appointment }               the quote's saved appointment (the latest booking)
 *  - { appointment: null }         an appointment is saved but could not be loaded (deleted, not found)
 *  - { appointment: null, subStatus } nothing saved, but the quote is marked Scheduled or Missed
 * A failed lookup never blocks booking: it falls through to the sub-status check.
 */
export const findExistingAppointment = async ({ ccId, quote }) => {
  let appointmentId = null
  try {
    const qwiResponse = await getQWIByQuoteId(quote.id)
    appointmentId = getQuoteAppointmentId(qwiResponse?.data)
  } catch (error) {
    // getQWIByQuoteId already logs; fall back to the sub-status below.
  }

  if (appointmentId) {
    try {
      const response = await getAppointmentDetails(ccId, appointmentId)
      if (response?.data?.id) return { appointment: response.data }
    } catch (error) {
      // getAppointmentDetails already logs; show the dialog without the row.
    }
    return { appointment: null }
  }

  if (BOOKED_SUB_STATUSES.includes(quote?.sub_status)) return { appointment: null, subStatus: quote.sub_status }
  return null
}

const normalizeVin = (vin) => (typeof vin === 'string' ? vin.trim().toUpperCase() : '')

/**
 * The vehicle's upcoming appointments, soonest first — for New Appointment on the Scheduler, which has no
 * quote to check. The scheduler's search matches the VIN anywhere (and skips cancelled appointments), so
 * only exact matches are kept. A failed lookup returns none: it must never block booking.
 */
export const findUpcomingAppointmentsForVin = async ({ ccId, organisationId, vin, now = dayjs() }) => {
  const target = normalizeVin(vin)
  if (!ccId || !organisationId || target.length !== 17) return []
  try {
    const response = await getEvents({
      ccId,
      organisationId,
      // A day either side of "today" covers every time zone; past ones are filtered out below.
      startDate: now.subtract(1, 'day').format('YYYY-MM-DD'),
      endDate: now.add(2, 'year').format('YYYY-MM-DD'),
      searchTerm: target
    })
    return (Array.isArray(response?.data) ? response.data : [])
      .filter((appointment) => normalizeVin(appointment?.vehicle?.vin) === target && isAppointmentUpcoming(appointment, now))
      .sort((a, b) => appointmentStartUtc(a).valueOf() - appointmentStartUtc(b).valueOf())
  } catch (error) {
    // getEvents already logs.
    return []
  }
}
